import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { isValidInput, validateUserInput } from "@/utils/security";
import { toast } from "sonner";

const formSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .refine(val => isValidInput(val), {
      message: "Name contains invalid characters",
    }),
  domain: z
    .string()
    .min(1, "Domain is required")
    .regex(/^(https?:\/\/)?[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](\.[a-zA-Z]{2,})+\/?$/, 
      "Please enter a valid domain (e.g., example.com)")
});

export type PixelFormValues = z.infer<typeof formSchema>;

interface CreatePixelFormProps {
  onSubmit: (data: PixelFormValues) => Promise<void>;
  isSubmitting: boolean;
}

export function CreatePixelForm({ onSubmit, isSubmitting }: CreatePixelFormProps) {
  const form = useForm<PixelFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      domain: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (!isSubmitting) {
      const timer = setTimeout(() => {
        form.reset({ name: "", domain: "" });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isSubmitting, form]);

  const handleInputChange = (field: keyof PixelFormValues, value: string) => {
    const validation = validateUserInput(value);
    if (!validation.isValid && validation.message) {
      toast.error(validation.message);
      return false;
    }
    return true;
  };

  const handleSubmit = async (data: PixelFormValues) => {
    if (isSubmitting) return;
    
    const nameValidation = validateUserInput(data.name);
    if (!nameValidation.isValid) {
      toast.error(nameValidation.message || "Invalid name input");
      return;
    }
    
    const domainValidation = validateUserInput(data.domain);
    if (!domainValidation.isValid) {
      toast.error(domainValidation.message || "Invalid domain input");
      return;
    }
    
    await onSubmit(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 px-2 pb-2">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pixel Name</FormLabel>
              <FormControl>
                <Input 
                  placeholder="E.g., Marketing Website" 
                  disabled={isSubmitting} 
                  {...field} 
                  onChange={(e) => {
                    const isValid = handleInputChange('name', e.target.value);
                    if (isValid) field.onChange(e);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="domain"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Website Domain</FormLabel>
              <FormControl>
                <Input 
                  placeholder="E.g., example.com" 
                  disabled={isSubmitting}
                  {...field}
                  onChange={(e) => {
                    const isValid = handleInputChange('domain', e.target.value);
                    if (isValid) field.onChange(e);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button 
          type="submit" 
          className="w-full bg-black hover:bg-black/90 text-white"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating Pixel..." : "Create Pixel"}
        </Button>
      </form>
    </Form>
  );
}
